import anime from "animejs";
import { fabric } from "fabric";
import { debounce } from "lodash";
import {
  BLOCK_REVEAL_IN_TOP,
  BLOCK_REVEAL_IN_BOTTOM,
  BLOCK_REVEAL_IN_LEFT,
  BLOCK_REVEAL_IN_RIGHT,
  BLOCK_REVEAL_OUT_TOP,
  BLOCK_REVEAL_OUT_BOTTOM,
  BLOCK_REVEAL_OUT_LEFT,
  BLOCK_REVEAL_OUT_RIGHT,
  GLITCH,
  DISCO,
  STOMP,
  BREATHE,
  GREAT_THINKER,
  SUNNY_MORNINGS,
  RISING_STRONG_IN,
  RISING_STRONG_OUT,
  MADE_WITH_LOVE,
  BEAUTIFUL_QUESTIONS,
  DOMINO_DREAMS,
  FIND_YOUR_ELEMENT,
  THURSDAY,
  HELLO_GOODBYE,
  COFFEE_MORNINGS,
  A_NEW_PRODUCTION_IN,
  A_NEW_PRODUCTION_OUT,
  FABRIC_VIDEO_ELEMENT,
  GROUP_LEVEL_ANIMATIONS,
} from "../../../details/constants";
import store from "../../../../store";
import RenderingHandler from "../RenderingHandler";
import { getAnimationConfig } from "./AnimationConfigHandler";
import { setDefaultClipPath } from "../../utils/ObjectUtil";

class AnimationHandler {
  constructor(handler) {
    this.handler = handler;
    this.timeline = null;
    this.animatedObjects = new Object();
    this.addedAnimateObjects = new Object();
    this.animationTargets = [];
    this.extraAnimationObjects = [];
    this.tasks = [];
  }

  playVideo = (videoObject) => {
    try {
      this.handler.startRequestAnimFrame();
      const videoElement = videoObject.element;
      videoElement.paused &&
        videoElement instanceof HTMLVideoElement &&
        videoElement.play();
    } catch (error) {
      console.error(`Error while playing video: ${error}`);
    }
  };

  stopVideo = (videoObject) => {
    try {
      this.handler.stopRequestAnimFrame();
      !videoObject.element.paused && videoObject.stop();
    } catch (error) {
      console.error(`Error while stopVideo video: ${error}`);
    }
  };

  pauseVideo = (videoObject) => {
    try {
      this.handler.stopRequestAnimFrame();
      !videoObject.paused && videoObject.pause();
    } catch (error) {
      console.error(`Error while pause video: ${error}`);
    }
  };

  applyZoomTransition = (obj) => {
    this.animatedObjects[obj.id] = {
      // ...this.animatedObjects[obj.id],
      scaleX: obj.scaleX,
      scaleY: obj.scaleY,
      originX: "left",
      originY: "top",
      left: obj.left,
      top: obj.top,
      opacity: obj.opacity,
    };
    const radian = (-obj.angle * Math.PI) / 180;
    const cos = Math.cos(radian);
    const sin = Math.sin(radian);
    let run = 0;
    let rise = 0;

    if (obj.angle === 90 || obj.angle === 270) {
      run = Math.abs(obj.aCoords.tr.y - obj.aCoords.tl.y) / 2;
      rise = Math.abs(obj.aCoords.bl.x - obj.aCoords.tl.x) / 2;
    } else {
      run =
        Math.abs((obj.aCoords.tr.x - obj.aCoords.tl.x) / Math.cos(radian)) / 2;
      rise =
        Math.abs((obj.aCoords.bl.y - obj.aCoords.tl.y) / Math.cos(radian)) / 2;
    }

    obj.set({
      originX: "center",
      originY: "center",
      left: obj.aCoords.tl.x + cos * run + sin * rise,
      top: obj.aCoords.tl.y + cos * rise - sin * run,
    });
    // this.handler.canvas.requestRenderAll();
  };

  attachEnterAnimations = (animations, obj, enterAnimeOptions, previewMode) => {
    enterAnimeOptions.forEach((option, index) => {
      if (index === 0 && obj.type === FABRIC_VIDEO_ELEMENT) {
        const videoOption = {
          ...option,
          option: {
            ...option.option,
            begin: () => {
              this.playVideo(obj);
            },
            // complete: () => {
            //   this.stopVideo(obj);
            // },
          },
        };
        this.timeline.add(
          videoOption.option,
          Math.max(
            videoOption.offset + (previewMode ? 0 : animations[0].start_time),
            0
          )
        );
      } else {
        this.timeline.add(
          option.option,
          Math.max(
            option.offset + (previewMode ? 0 : animations[0].start_time),
            0
          )
        );
      }
    });
  };

  getMotionOption = (
    animation,
    obj,
    originalObject,
    animationTargets,
    isSinglePreview,
    loopEnabled = false,
    loopOption = undefined
  ) => {
    let animeOptions = [],
      addedGroupLevelAnimationObjects = [],
      addedCharacterLevelAnimationObjects = [];

    if (animation.type === FIND_YOUR_ELEMENT) {
      animationTargets[`${obj.id}_bottomLine`] = 0;
    } else if (animation.type === THURSDAY) {
      animationTargets[`${obj.id}_bottomLine`] = 0;
      animationTargets[`${obj.id}_topLine`] = 0;
    } else if (animation.type === HELLO_GOODBYE) {
      animationTargets[`${obj.id}_leftLine`] = 0;
    }

    if (GROUP_LEVEL_ANIMATIONS.includes(animation.type)) {
      if (
        animation.type.includes("zoom") ||
        animation.type === STOMP ||
        animation.type === BREATHE
      ) {
        this.applyZoomTransition(obj);
      }
      animeOptions = this.getGroupLevelAnimations(
        obj,
        originalObject,
        animation,
        isSinglePreview,
        loopEnabled,
        loopOption
      );
    } else {
      addedCharacterLevelAnimationObjects =
        this.getCharacterLevelAnimationAddedObjects(
          obj,
          originalObject,
          animation
        );

      animeOptions = this.getCharacterLevelAnimations(
        obj,
        originalObject,
        animation,
        addedCharacterLevelAnimationObjects,
        animationTargets,
        isSinglePreview,
        loopEnabled,
        loopOption
      );
    }
    return {
      animeOptions,
      addedGroupLevelAnimationObjects,
      addedCharacterLevelAnimationObjects,
    };
  };

  getStartAnimation = (animations, obj, previewMode, animationTargets) => {
    if (previewMode) {
      return;
    }

    if (animations[0].start_time === 0) {
      return 0;
    }

    this.timeline.add(
      {
        targets: obj,
        keyframes: [
          {
            opacity: 0,
            duration: 0,
          },
          { opacity: 0, duration: animations[0].start_time },
        ],
      },
      0
    );
  };

  getEndAnimation = (animations, obj, artboardDuration, animationTargets) => {
    const endTime = animations[animations.length - 1].end_time;
    let endAnimeOptions = [];

    if (endTime === artboardDuration * 1000) {
      return;
    }

    endAnimeOptions = [
      {
        option: {
          targets: animationTargets,
          [`${obj.id}_opacity`]: [0, 0],
          duration: artboardDuration * 1000 - endTime,
          begin: () => {
            if (obj.type === FABRIC_VIDEO_ELEMENT) {
              this.stopVideo(obj);
            }
          },
          update: () => {
            obj.set({
              opacity: animationTargets[`${obj.id}_opacity`],
            });
            // this.handler.canvas.renderAll();
          },
        },
        offset: 0,
      },
    ];

    if (this.addedAnimateObjects[obj.id]) {
      Object.keys(this.addedAnimateObjects[obj.id]).forEach((key) => {
        if (key.includes("letters")) {
          endAnimeOptions.push({
            option: {
              targets: animationTargets[`${obj.id}_lettersOpacity`],
              opacity: [0, 0],
              duration: artboardDuration * 1000 - endTime,
              update: () => {
                if (
                  this.addedAnimateObjects[obj.id][key] &&
                  animationTargets[`${obj.id}_lettersOpacity`]
                ) {
                  this.addedAnimateObjects[obj.id][key]?.forEach(
                    (item, index) => {
                      item.set({
                        opacity:
                          animationTargets[`${obj.id}_lettersOpacity`][index]
                            .opacity,
                      });
                    }
                  );
                }
                // this.handler.canvas.renderAll();
              },
            },
            offset: 0,
          });
        } else if (key.includes("letterGroup")) {
          return;
        } else {
          endAnimeOptions.push({
            option: {
              targets: animationTargets,
              [`${obj.id}_${key}`]: [0, 0],
              duration: artboardDuration * 1000 - endTime,
              update: () => {
                this.addedAnimateObjects[obj.id][key].set({
                  opacity: animationTargets[`${obj.id}_${key}`],
                });
                // this.handler.canvas.renderAll();
              },
            },
            offset: 0,
          });
        }
      });
    }

    if (endAnimeOptions?.length) {
      endAnimeOptions.forEach((option) => {
        this.timeline.add(option.option, Math.max(option.offset + endTime, 0));
      });
    }
  };

  getStillAnimation = (
    groupLevelAnimation,
    stillAnimation,
    obj,
    originalObject,
    animationTargets
  ) => {
    let stillAnimeOptions = [];

    if (groupLevelAnimation) {
      stillAnimeOptions = [
        {
          option: {
            targets: obj,
            keyframes: [
              {
                opacity: originalObject.opacity,
                duration: 1,
              },
              {
                opacity: originalObject.opacity,
                duration: stillAnimation.trans_duration,
              },
            ],
          },
          offset: 0,
        },
      ];
    } else {
      if (this.addedAnimateObjects[obj.id]) {
        stillAnimeOptions.push({
          option: {
            targets: this.addedAnimateObjects[obj.id],
            keyframes: [
              {
                opacity: originalObject.opacity,
                duration: 1,
              },
              {
                opacity: originalObject.opacity,
                duration: stillAnimation.trans_duration,
              },
            ],
          },
          offset: 0,
        });
      }
    }

    if (stillAnimeOptions?.length) {
      stillAnimeOptions.forEach((option) => {
        this.timeline.add(
          option.option,
          Math.max(option.offset + stillAnimation.start_time, 0)
        );
      });
    }
  };

  getLayerAnimationOptions = (
    obj,
    originalObject,
    animations,
    previewMode = false,
    artboardDuration
  ) => {
    if (!animations?.length) {
      // if there is no animation then return
      return;
    }

    if (obj.editable === false) {
      return;
    }

    let addedCharacterLevelAnimationObjects = [];
    let addedGroupLevelAnimationObjects = [];
    let enterAnimeOptions = [];
    let exitAnimeOptions = [];

    const animationTargets = new Object();
    animationTargets[`${obj.id}_opacity`] = 0;
    if (obj.type === "text" || obj.type === "textbox") {
      animationTargets[`${obj.id}_lettersOpacity`] = obj.text
        .split("")
        .map((item, index) => ({
          opacity: 0,
          index,
        }));
    }

    if (animations.length === 1 && animations[0].type === "still") {
      this.getStartAnimation(animations, obj, previewMode, animationTargets);
      enterAnimeOptions = this.getNoneAnimation(
        obj,
        animations[0],
        artboardDuration,
        animationTargets
      );
      enterAnimeOptions.forEach((option) => {
        this.timeline.add(option.option, Math.max(option.offset, 0));
      });
    } else if (animations.length === 1 && animations[0].type !== "still") {
      this.getStartAnimation(animations, obj, previewMode, animationTargets);

      const enterMotionOption = this.getMotionOption(
        animations[0],
        obj,
        originalObject,
        animationTargets,
        previewMode,
        false
      );
      ({
        animeOptions: enterAnimeOptions,
        addedGroupLevelAnimationObjects,
        addedCharacterLevelAnimationObjects,
      } = enterMotionOption);
      this.attachEnterAnimations(
        animations,
        obj,
        enterAnimeOptions,
        previewMode,
        animationTargets
      );
    } else if (
      animations.length === 2 &&
      animations[0].motion === "enter" &&
      animations[1].type === "still"
    ) {
      this.getStartAnimation(animations, obj, previewMode, animationTargets);

      const enterMotionOption = this.getMotionOption(
        animations[0],
        obj,
        originalObject,
        animationTargets,
        previewMode,
        animations[0].loop,
        animations[1]
      );
      ({
        animeOptions: enterAnimeOptions,
        addedGroupLevelAnimationObjects,
        addedCharacterLevelAnimationObjects,
      } = enterMotionOption);
      this.attachEnterAnimations(
        animations,
        obj,
        enterAnimeOptions,
        previewMode,
        animationTargets
      );

      if (!previewMode) {
        if (!animations[0].loop) {
          this.getStillAnimation(
            GROUP_LEVEL_ANIMATIONS.includes(animations[0].type),
            animations[1],
            obj,
            originalObject,
            animationTargets
          );
        }
        this.getEndAnimation(
          animations,
          obj,
          artboardDuration,
          animationTargets
        );
      }
    } else if (
      animations.length === 2 &&
      animations[0].type === "still" &&
      animations[1].motion === "exit"
    ) {
      this.getStartAnimation(animations, obj, previewMode, animationTargets);

      this.getStillAnimation(
        GROUP_LEVEL_ANIMATIONS.includes(animations[0].type),
        animations[0],
        obj,
        originalObject,
        animationTargets
      );

      const exitMotionOption = this.getMotionOption(
        animations[1],
        obj,
        originalObject,
        animationTargets,
        previewMode
      );
      ({
        animeOptions: exitAnimeOptions,
        addedGroupLevelAnimationObjects,
        addedCharacterLevelAnimationObjects,
      } = exitMotionOption);

      exitAnimeOptions.forEach((option, index) => {
        if (index === 0 && obj.type === FABRIC_VIDEO_ELEMENT) {
          const videoOption = {
            ...option,
            option: {
              ...option.option,
              begin: () => {
                this.playVideo(obj);
              },
            },
          };
          this.timeline.add(
            videoOption.option,
            Math.max(videoOption.offset + animations[1].start_time, 0)
          );
        } else {
          this.timeline.add(
            option.option,
            Math.max(option.offset + animations[1].start_time, 0)
          );
        }
      });

      this.getEndAnimation(animations, obj, artboardDuration, animationTargets);
    } else if (
      animations.length === 3 &&
      animations[0].motion === "enter" &&
      animations[1].type === "still" &&
      animations[2].motion === "exit"
    ) {
      this.getStartAnimation(animations, obj, previewMode, animationTargets);

      const enterMotionOption = this.getMotionOption(
        animations[0],
        obj,
        originalObject,
        animationTargets,
        previewMode,
        animations[0].loop,
        animations[1]
      );
      ({
        animeOptions: enterAnimeOptions,
        addedGroupLevelAnimationObjects,
        addedCharacterLevelAnimationObjects,
      } = enterMotionOption);
      this.attachEnterAnimations(
        animations,
        obj,
        enterAnimeOptions,
        previewMode,
        animationTargets
      );

      if (!previewMode) {
        if (!animations[0].loop) {
          this.getStillAnimation(
            GROUP_LEVEL_ANIMATIONS.includes(animations[0].type),
            animations[1],
            obj,
            originalObject,
            animationTargets
          );
        }

        if (
          GROUP_LEVEL_ANIMATIONS.includes(animations[2].type) &&
          GROUP_LEVEL_ANIMATIONS.includes(animations[0].type)
        ) {
          exitAnimeOptions = this.getGroupLevelAnimations(
            obj,
            originalObject,
            animations[2],
            previewMode
          );
        } else if (
          !GROUP_LEVEL_ANIMATIONS.includes(animations[2].type) &&
          !GROUP_LEVEL_ANIMATIONS.includes(animations[0].type)
        ) {
          exitAnimeOptions = this.getCharacterLevelAnimations(
            obj,
            originalObject,
            animations[2],
            addedCharacterLevelAnimationObjects,
            animationTargets,
            previewMode
          );
        } else if (
          !GROUP_LEVEL_ANIMATIONS.includes(animations[0].type) &&
          GROUP_LEVEL_ANIMATIONS.includes(animations[2].type)
        ) {
          exitAnimeOptions = this.getGroupLevelAnimations(
            obj,
            originalObject,
            animations[2],
            previewMode
          );
          if (exitAnimeOptions && exitAnimeOptions.length) {
            exitAnimeOptions[0] = {
              ...exitAnimeOptions[0],
              option: {
                ...exitAnimeOptions[0].option,
                begin: () => {
                  if (this.addedAnimateObjects[obj.id]) {
                    Object.keys(this.addedAnimateObjects[obj.id]).forEach(
                      (key) => {
                        if (key === "letters") {
                          this.addedAnimateObjects[obj.id]["letters"].forEach(
                            (letter) => {
                              this.handler.canvas.remove(letter);
                            }
                          );
                        } else {
                          this.handler.canvas.remove(
                            this.addedAnimateObjects[obj.id][key]
                          );
                        }
                      }
                    );
                  }
                },
              },
            };
          }
        } else {
          addedCharacterLevelAnimationObjects =
            this.getCharacterLevelAnimationAddedObjects(
              obj,
              originalObject,
              animations[2]
            );
          exitAnimeOptions = this.getCharacterLevelAnimations(
            obj,
            originalObject,
            animations[2],
            addedCharacterLevelAnimationObjects,
            animationTargets,
            previewMode
          );
          if (exitAnimeOptions && exitAnimeOptions.length) {
            exitAnimeOptions[0] = {
              ...exitAnimeOptions[0],
              option: {
                ...exitAnimeOptions[0].option,
                begin: () => {
                  this.animatedObjects[obj.id] = { opacity: obj.opacity };
                  obj.set({
                    opacity: 0,
                  });
                },
              },
            };
          }
        }

        if (exitAnimeOptions?.length) {
          exitAnimeOptions.forEach((option) => {
            this.timeline.add(
              option.option,
              Math.max(option.offset + animations[2].start_time, 0)
            );
          });
        }

        this.getEndAnimation(
          animations,
          obj,
          artboardDuration,
          animationTargets
        );
      }
    }

    this.animationTargets.push(animationTargets);

    if (previewMode) {
      obj.set({
        hasControls: false,
        hasBorders: false,
        lockMovementX: true,
        lockMovementY: true,
        hoverCursor: "pointer",
        /**
         * MARK: Check opacity need
         */
        // opacity: 0,
      });
      return;
    }

    obj.set({
      hasControls: false,
      hasBorders: false,
      lockMovementX: true,
      lockMovementY: true,
      hoverCursor: "pointer",
      /**
       * MARK: Check opacity need
       */
      // opacity:
      //   animations.length === 2 &&
      //   animations[0].type === "still" &&
      //   animations[1].motion === "exit"
      //     ? 1
      //     : animations[0].start_time === 0
      //     ? 1
      //     : 0,
    });
  };

  applyLayerAnimations = (animations) => {
    this.handler.set("animation", animations);
  };

  debouncedApplyAnimations = debounce(this.applyLayerAnimations, 1000);

  previewAndUpdate = (
    activeObj,
    newAnimations,
    animationIndex = 0,
    update,
    artboardDuration
  ) => {
    return new Promise((resolve, reject) => {
      // Stop currently playing animations
      this.stopAll();
      // Cancel any ongoing debounced events
      if (update) this.debouncedApplyAnimations.cancel();

      const { id } = activeObj;
      if (this.timeline) {
        this.timeline.play();
        resolve();
        return;
      }
      const activeObject = this.handler.canvas.getActiveObject();
      let findObject;
      if (activeObject && activeObject.id === id) {
        findObject = activeObject;
      } else {
        findObject = this.handler.findById(id);
      }
      if (!findObject) {
        resolve();
        return;
      }
      this.timeline = anime.timeline({
        autoplay: false,
        begin: () => {
          RenderingHandler.startRepaintingCanvas(this.handler.canvas);
        },
        complete: () => {
          RenderingHandler.stopRepaintingCanvas(this.handler.canvas);
        },
      });

      const objectClone = fabric.util.object.clone(findObject);

      this.getLayerAnimationOptions(
        findObject,
        objectClone,
        animationIndex >= 0 ? [newAnimations[animationIndex]] : newAnimations,
        animationIndex >= 0,
        artboardDuration
      );

      if (!this.timeline) {
        resolve();
        return;
      }

      if (!this.timeline.duration) {
        this.timeline = null;
        resolve();
        return;
      }

      this.handler.interactionHandler.preview();
      // this.handler.canvas.requestRenderAll();
      this.timeline.play();
      this.timeline.finished.then(() => {
        this.resetAnimation(findObject);
        if (findObject.type === FABRIC_VIDEO_ELEMENT) {
          this.stopVideo(findObject);
        }
        this.handler.interactionHandler.selection();
        // If update call then debounce the animations
        if (update) this.debouncedApplyAnimations(newAnimations);
        resolve(newAnimations);
      });
    });
  };

  /**
   * This method previews current artboard
   * @returns
   */
  previewCurrentArtboard = (artboardDuration, presetPreviewMode = false) => {
    return new Promise((resolve, reject) => {
      this.stopAll(); // Stop all ongoing animations
      const allLayers = this.handler.getObjects();

      if (this.timeline) {
        this.timeline.play();
        allLayers.forEach((findObject) => {
          if (findObject.type === FABRIC_VIDEO_ELEMENT) {
            this.playVideo(findObject);
          }
        });

        resolve();
        return;
      }

      this.timeline = anime.timeline({
        autoplay: false,
        begin: () => {
          RenderingHandler.startRepaintingCanvas(this.handler.canvas);
        },
        complete: () => {
          RenderingHandler.stopRepaintingCanvas(this.handler.canvas);
        },
      });

      allLayers
        .filter((obj) => obj.editable !== false)
        .forEach((findObject) => {
          let animationSet;
          let animations = findObject.animation;
          if (findObject.type === FABRIC_VIDEO_ELEMENT) {
            if (animations === undefined || animations?.length === 0) {
              animationSet = [
                {
                  autoplay: true,
                  delay: 0,
                  direction: "normal",
                  easing: "easeOutQuart",
                  end_time:
                    Math.min(findObject.endTime, artboardDuration) * 1000,
                  loop: false,
                  motion: "enter",
                  start_time: 0,
                  trans_duration:
                    Math.min(
                      findObject.endTime - findObject.startTime,
                      artboardDuration
                    ) * 1000,
                  type: "still",
                },
              ];
            } else {
              animationSet = animations;
            }
          } else {
            if (!animations) {
              return;
            }
            if (presetPreviewMode) {
              animationSet = [animations[0]];
            } else {
              animationSet = animations;
            }
          }

          const objectClone = fabric.util.object.clone(findObject);

          this.getLayerAnimationOptions(
            findObject,
            objectClone,
            animationSet,
            false,
            artboardDuration
          );
        });

      if (!this.timeline.duration) {
        this.timeline = null;
        const updatedLayers = this.handler.getLayersPayload();
        resolve(updatedLayers);
        return;
      }
      this.handler?.interactionHandler?.preview();
      // this.handler.canvas.requestRenderAll();

      this.timeline.play();

      this.timeline.finished.then(() => {
        allLayers.forEach((layerObject) => {
          this.resetAnimation(layerObject);
          if (layerObject.type === FABRIC_VIDEO_ELEMENT) {
            this.stopVideo(layerObject);
          }
        });
        this.handler?.interactionHandler?.selection();
        resolve();
      });
    });
  };

  seekToPosition = (point, artboardDuration, shouldEagerPlay = true) => {
    const allLayers = this.handler.getObjects();
    if (!this.timeline) {
      this.timeline = anime.timeline({
        autoplay: false,
        begin: () => {
          RenderingHandler.startRepaintingCanvas(this.handler.canvas);
        },
        complete: () => {
          RenderingHandler.stopRepaintingCanvas(this.handler.canvas);
        },
      });

      allLayers.forEach((findObject) => {
        let animationSet;
        let animations = findObject.animation;
        if (findObject.type === FABRIC_VIDEO_ELEMENT) {
          if (animations === undefined || animations?.length === 0) {
            animationSet = [
              {
                autoplay: true,
                delay: 0,
                direction: "normal",
                easing: "easeOutQuart",
                end_time: Math.min(findObject.endTime, artboardDuration) * 1000,
                loop: false,
                motion: "enter",
                start_time: 0,
                trans_duration:
                  Math.min(
                    findObject.endTime - findObject.startTime,
                    artboardDuration
                  ) * 1000,
                type: "still",
              },
            ];
          } else {
            animationSet = animations;
          }
        } else {
          if (!animations) {
            return;
          }
          animationSet = animations;
        }

        const objectClone = fabric.util.object.clone(findObject);

        this.getLayerAnimationOptions(
          findObject,
          objectClone,
          animationSet,
          false,
          artboardDuration
        );
      });
    }

    if (!this.timeline.duration) {
      this.timeline = null;
      return;
    }

    this.handler?.interactionHandler?.preview();
    // this.handler.canvas.requestRenderAll();

    if (point !== undefined) {
      this.timeline.seek(point * 1000);
      if (shouldEagerPlay) {
        if (this.timeline.paused) {
          this.timeline.play();
        }
      } else {
        this.timeline.play();
        setTimeout(() => {
          if (this.timeline) {
            this.handler?.interactionHandler?.selection();
            this.timeline.pause();
          }
        }, 1);
      }

      allLayers?.forEach((findObject) => {
        if (findObject.type === FABRIC_VIDEO_ELEMENT) {
          let animations = findObject.animation;
          const videoEl = findObject.element;
          let currentTime = 0;
          let startTime = 0;
          if (!animations) {
            if (videoEl.paused && point > startTime) {
              videoEl.play();
            }
            return;
          }

          if (shouldEagerPlay) {
            startTime =
              (animations && animations.length ? animations[0].start_time : 0) /
              1000;

            if (videoEl.paused && point > startTime) {
              videoEl.play();
            }
          } else {
            currentTime = Math.max(
              0,
              point -
                (animations && animations.length
                  ? animations[0].start_time
                  : 0) /
                  1000
            );
            videoEl.currentTime = Math.round(currentTime * 10) / 10;
            setTimeout(() => {
              videoEl.pause();
            }, 1);
          }
        }
      });
    }
    return;
  };

  /**
   * Pause the animation
   * @param {string} id
   * @returns
   */
  pause = (id) => {
    const animatedObject = this.animatedObjects[id];
    if (!animatedObject) {
      return;
    }
    if (this.timeline) {
      this.timeline.pause();
    }
  };

  /**
   * Pause all animations
   */
  pauseAll = () => {
    if (!this.timeline) {
      return;
    }
    this.timeline.pause();
    Object.keys(this.animatedObjects).forEach((key) => {
      const findObject = this.handler.findById(key);
      if (!findObject) {
        return;
      }
      if (findObject.type === FABRIC_VIDEO_ELEMENT) {
        this.pauseVideo(findObject.element);
      }
    });
  };

  /**
   * Stop the animation
   * @param {string} id
   * @param {boolean} [hasControls=true]
   * @returns
   */
  stop = (id, effect, hasControls = true) => {
    const animatedObject = this.animatedObjects[id];
    if (!animatedObject) {
      return;
    }
    const findObject = this.handler.findById(id);
    if (!findObject) {
      return;
    }
    if (this.timeline) {
      this.timeline.pause();
    }
    this.resetAnimation(findObject);
  };

  /**
   * Stop all animations
   */
  // TODO: Stop video
  stopAll = () => {
    /**
     * Perform postprocessing
     * For eg. To remove extra objects added for animation
     */
    this.doPostAnimationTasks();

    if (!this.timeline) {
      return;
    }

    this.timeline.pause();
    this.timeline.seek(0);
    Object.keys(this.animatedObjects).forEach((key) => {
      const findObject = this.handler.findById(key);
      if (!findObject) {
        return;
      }
      this.resetAnimation(findObject);
      if (findObject.type === FABRIC_VIDEO_ELEMENT) {
        this.stopVideo(findObject);
      }
    });

    if (this.handler.interactionHandler) {
      this.handler.interactionHandler.selection();
    }
  };

  /**
   * Restart the animation
   * @param {string} id
   * @returns
   */
  restart = (id) => {
    const findObject = this.handler.findById(id);
    if (!findObject) {
      return;
    }
    if (!findObject.anime) {
      return;
    }
    this.stop(id);
    this.play(id, false, "");
  };

  /**
   * Reset animation
   *
   * @param {FabricObject} obj
   * @param {boolean} [hasControls=true]
   * @returns
   */
  resetAnimation = (obj, hasControls = true) => {
    if (this.timeline) {
      this.animationTargets.forEach((target) => {
        this.timeline.remove(target);
      });
    }

    if (this.addedAnimateObjects[obj.id]) {
      Object.keys(this.addedAnimateObjects[obj.id]).forEach((key) => {
        if (key.includes("letters")) {
          this.addedAnimateObjects[obj.id][key].forEach((letter) => {
            this.handler.canvas.remove(letter);
          });
        } else {
          this.handler.canvas.remove(this.addedAnimateObjects[obj.id][key]);
        }
      });
      this.addedAnimateObjects = new Object();
    }

    if (obj.editable) {
      this.animatedObjects[obj.id] = {
        ...this.animatedObjects[obj.id],
        hasControls: true,
        hasBorders: true,
        lockMovementX: false,
        lockMovementY: false,
        hoverCursor: hasControls ? "move" : "pointer",
      };
    } else {
      this.animatedObjects[obj.id] = {
        ...this.animatedObjects[obj.id],
        hasControls: true,
        lockMovementX: true,
        lockMovementY: true,
        hoverCursor: "pointer",
      };
    }
    obj.set(this.animatedObjects[obj.id]);

    // this.handler.canvas.renderAll();
    delete this.animatedObjects[obj.id];
    this.animationTargets = [];
    this.timeline = null;
  };

  getNoneAnimation = (obj, effect, artboardDuration, animationTargets) => {
    const canvas = this.handler.canvas;
    animationTargets[`${obj.id}_opacity`] = 0;

    this.animatedObjects[obj.id] = { opacity: obj.opacity };
    let duration;
    if (obj.type === FABRIC_VIDEO_ELEMENT) {
      duration = Math.min(
        effect.trans_duration,
        (obj.endTime - obj.startTime) * 1000
      );
    } else {
      duration = effect.trans_duration;
    }
    const originOpacity = obj.opacity;

    const enterAnimeOptions = [
      {
        option: {
          loop: false,
          targets: animationTargets,
          [`${obj.id}_opacity`]: [0],
          duration: obj.startTime,
          update: () => {
            obj.set({
              opacity: animationTargets[`${obj.id}_opacity`],
            });
            // canvas.renderAll();
          },
        },
        offset: 0,
      },
      {
        option: {
          loop: false,
          targets: animationTargets,
          [`${obj.id}_opacity`]: [originOpacity],
          duration: 1,
          update: () => {
            obj.set({
              opacity: animationTargets[`${obj.id}_opacity`],
            });
            // canvas.renderAll();
          },
        },
        offset: effect.start_time,
      },
      {
        option: {
          loop: false,
          targets: animationTargets,
          [`${obj.id}_opacity`]: [originOpacity],
          duration: duration - 1,
          begin: () => {
            if (obj.type === FABRIC_VIDEO_ELEMENT) {
              this.playVideo(obj);
            }
          },
          update: () => {
            obj.set({
              opacity: animationTargets[`${obj.id}_opacity`],
            });
            // canvas.renderAll();
          },
          complete: () => {
            if (obj.type === FABRIC_VIDEO_ELEMENT) {
              this.stopVideo(obj);
            }
          },
        },
        offset: effect.start_time + 1,
      },
      {
        option: {
          loop: false,
          targets: animationTargets,
          [`${obj.id}_opacity`]: [0],
          duration: 1,
          update: () => {
            obj.set({
              opacity: animationTargets[`${obj.id}_opacity`],
            });
            // canvas.renderAll();
          },
        },
        offset: effect.start_time + duration,
      },
      {
        option: {
          loop: false,
          targets: animationTargets,
          [`${obj.id}_opacity`]: [0],
          duration:
            artboardDuration * 1000 -
            effect.trans_duration -
            effect.start_time -
            1,
          update: () => {
            obj.set({
              opacity: animationTargets[`${obj.id}_opacity`],
            });
            // canvas.renderAll();
          },
        },
        offset: effect.start_time + duration + 1,
      },
    ];
    return enterAnimeOptions;
  };

  /**
   * Get animation option
   *
   * @param {FabricObject} fabricObject
   * @param {boolean} [hasControls]
   * @returns
   */
  getGroupLevelAnimations = (
    fabricObject,
    fabricObjectClone,
    effect,
    isSinglePreview,
    loopEnabled,
    loopOption
  ) => {
    if (!effect) {
      return;
    }

    const params = {
      fabricObject,
      fabricObjectClone,
      effect,
      animatedObjects: this.animatedObjects,
      loopEnabled,
      loopOption,
      workarea: this.handler.workarea,
      isSinglePreview,
      storeOriginalProperties: true,
      extras: this.getExtraAnimationParams(
        fabricObject,
        effect.type,
        isSinglePreview,
        {}
      ),
    };

    return getAnimationConfig(params);
  };

  updateArtboardAnimation = (animation) => {
    const { canvas, workarea } = this.handler;
    if (!workarea) {
      return;
    }
    let newAnimation = {
      duration: animation?.duration
        ? animation.duration
        : workarea.animation?.duration,
      transition: animation?.transition
        ? animation.transition
        : workarea.animation?.transition,
      preset: animation?.preset ? animation.preset : workarea.animation?.preset,
    };
    workarea.set({
      animation: newAnimation,
    });
    canvas.fire("object:modified", {
      target: workarea,
    });
  };

  addOrRemoveTransition = (animation) => {
    const { canvas, workarea } = this.handler;
    if (!workarea) {
      return;
    }
    workarea.set({
      animation: animation,
    });
    canvas.fire("object:modified", {
      target: workarea,
    });
  };

  getCharactersAndGroup = (obj, originalObject, effect, save = true) => {
    const state = store.getState();
    const radian = (-obj.angle * Math.PI) / 180;
    const width =
      Math.abs((obj.aCoords.tr.x - obj.aCoords.tl.x) / Math.cos(radian)) *
      obj.scaleX;
    const height =
      Math.abs((obj.aCoords.bl.y - obj.aCoords.tl.y) / Math.cos(radian)) *
      obj.scaleY;
    const groupFormat = obj
      ? {
          left: obj.left - obj.width / 2,
          top: obj.top - (obj.height * obj.scaleY * 3) / 2,
          width: width * 2,
          height: height * 4,
          angle: obj.angle,
          borderColor: obj.editingBorderColor,
          flipX: obj.flipX,
          flipY: obj.flipY,
          opacity: 1,
          skewX: obj.skewX,
          skewY: obj.skewY,
          strokeWidth: obj.strokeWidth,
          strokeMiterLimit: obj.strokeMiterLimit,
          visible: obj.visible,
          hasControls: false,
          hasBorders: false,
        }
      : {};
    const lettersGroup = new fabric.Group(undefined, groupFormat);
    this.handler.canvas.add(lettersGroup);

    this.animatedObjects[obj.id] = {
      ...this.animatedObjects[obj.id],
      opacity: originalObject.opacity,
    };

    const backgroundRect = new fabric.Rect({
      left: 0,
      top: 0,
      width: obj.width * obj.scaleX,
      height: obj.height * obj.scaleY,
      flipX: obj.flipX,
      flipY: obj.flipY,
      fill: obj.backgroundColor,
      originX: "center",
      originY: "center",
      hasControls: false,
      hasBorders: false,
    });
    lettersGroup.add(backgroundRect);

    lettersGroup.moveTo(state.layerstore.layers[obj.id]?.order + 1);

    let letters = [];
    if (obj.textLines && obj.textLines.length > 0) {
      obj.textLines.forEach((textLine, lineIndex) => {
        const textLength = obj._measureLine(lineIndex).width;
        const lineHeight = obj.getHeightOfLine(lineIndex);
        let lineYPosition = 0;
        for (let i = 0; i < lineIndex; i++) {
          lineYPosition += obj.scaleY * obj.getHeightOfLine(i);
        }
        if (effect.type === COFFEE_MORNINGS) {
          lineYPosition += lineHeight * obj.scaleY;
        } else {
          lineYPosition += (lineHeight * obj.scaleY) / 2;
        }
        if (lineYPosition < (obj.height * obj.scaleY) / 2) {
          lineYPosition = -((obj.height * obj.scaleY) / 2) + lineYPosition;
        } else {
          lineYPosition = lineYPosition - (obj.height * obj.scaleY) / 2;
        }
        const charHeights = obj.__charBounds[lineIndex].map(
          (item) => item.height
        );
        charHeights.pop();
        const maxHeight = Math.max(...charHeights);
        const lineLetters = textLine.split("").map((char, charIndex) => {
          let left;
          if (obj.textAlign === "left") {
            left =
              -width / 2 +
              obj.__charBounds[lineIndex][charIndex].left * obj.scaleX;
          } else if (obj.textAlign === "center") {
            left =
              obj.__charBounds[lineIndex][charIndex].left * obj.scaleX -
              (textLength * obj.scaleX) / 2;
          } else {
            left =
              width / 2 -
              textLength * obj.scaleX +
              obj.__charBounds[lineIndex][charIndex].left * obj.scaleX;
          }
          const charStyle = obj._getStyleDeclaration(lineIndex, charIndex);
          const letterConfig = {
            originY: effect.type === COFFEE_MORNINGS ? "bottom" : "center",
            originX: effect.type === COFFEE_MORNINGS ? "left" : "center",
            width: obj.__charBounds[lineIndex][charIndex].width * obj.scaleX,
            height: lineHeight * obj.scaleY,
            left:
              effect.type === COFFEE_MORNINGS
                ? left
                : left +
                  (obj.__charBounds[lineIndex][charIndex].width * obj.scaleX) /
                    2,
            top:
              lineYPosition +
              (effect.type === COFFEE_MORNINGS
                ? 0
                : (maxHeight * obj.scaleY -
                    obj.__charBounds[lineIndex][charIndex].height *
                      obj.scaleY) /
                  3.2),
            fontFamily: obj.fontFamily,
            fontSize: obj.fontSize,
            lineHeight: obj.lineHeight,
            fontWeight: obj.fontWeight,
            fontStyle: obj.fontStyle,
            fill: obj.fill,
            fillRule: obj.fillRule,
            paintFirst: obj.paintFirst,
            opacity: 0,
            zoomX: obj.zoomX,
            zoomY: obj.zoomY,
            scaleX: obj.scaleX,
            scaleY: obj.scaleY,
            borderColor: obj.borderColor,
            backgroundColor: obj.backgroundColor,
            shadow: obj.shadow,
            stroke: obj.stroke,
            strokeDashArray: obj.strokeDashArray,
            strokeDashOffset: obj.strokeDashOffset,
            strokeLineCap: obj.strokeLineCap,
            strokeLineJoin: obj.strokeLineJoin,
            strokeMiterLimit: obj.strokeMiterLimit,
            strokeUniform: obj.strokeUniform,
            textBackgroundColor: obj.textBackgroundColor,
            underline: obj.underline,
            hasControls: false,
            hasBorders: false,
            ...charStyle,
          };

          if (effect.type !== GLITCH && effect.type !== DISCO) {
            letterConfig.visible = false;
          }

          return new fabric.Text(char, letterConfig);
        });
        letters = [...letters, ...lineLetters];
      });
    }

    letters.forEach((letter) => {
      lettersGroup.add(letter);
    });

    if (save) {
      this.addedAnimateObjects[obj.id] = {
        ...this.addedAnimateObjects[obj.id],
        letters: letters,
      };
      this.addedAnimateObjects[obj.id] = {
        ...this.addedAnimateObjects[obj.id],
        backgroundRect: backgroundRect,
      };
      this.addedAnimateObjects[obj.id] = {
        ...this.addedAnimateObjects[obj.id],
        letterGroup: lettersGroup,
      };
    }

    return { letters, lettersGroup };
  };

  getCharacterLevelAnimationAddedObjects = (obj, originalObject, effect) => {
    const { letters, lettersGroup } = this.getCharactersAndGroup(
      obj,
      originalObject,
      effect
    );

    const radian = (-obj.angle * Math.PI) / 180;
    const cos = Math.cos(radian);
    const sin = Math.sin(radian);
    const run = (obj.width * obj.scaleX) / 2;
    const rise = (obj.height * obj.scaleY) / 2;
    lettersGroup.set({
      originX: "center",
      originY: "center",
      left: cos * run + sin * rise + obj.left,
      top: cos * rise - sin * run + obj.top,
    });

    if (effect.type === FIND_YOUR_ELEMENT) {
      const bottomLine = new fabric.Line(
        [
          obj.aCoords.br.x - obj.width,
          obj.aCoords.br.y,
          obj.aCoords.br.x,
          obj.aCoords.br.y,
        ],
        {
          fill: obj.fill,
          stroke: obj.fill,
          strokeWidth: 4,
          originX: "right",
          selectable: false,
          opacity: 0,
          angle: obj.angle,
          visible: false,
        }
      );
      this.addedAnimateObjects[obj.id] = {
        ...this.addedAnimateObjects[obj.id],
        bottomLine: bottomLine,
      };
      return {
        letters,
        lettersGroup,
        bottomLine,
      };
    }
    if (effect.type === THURSDAY) {
      const topLine = new fabric.Line(
        [obj.left, obj.top, obj.left + obj.width, obj.top],
        {
          fill: obj.fill,
          stroke: obj.fill,
          strokeWidth: 4,
          originX: "left",
          selectable: false,
          opacity: 0,
          angle: obj.angle,
          visible: false,
        }
      );
      const bottomLine = new fabric.Line(
        [
          obj.aCoords.bl.x,
          obj.aCoords.bl.y,
          obj.aCoords.bl.x + obj.width,
          obj.aCoords.bl.y,
        ],
        {
          fill: obj.fill,
          stroke: obj.fill,
          strokeWidth: 4,
          originX: "left",
          selectable: false,
          opacity: 0,
          angle: obj.angle,
          visible: false,
        }
      );
      this.addedAnimateObjects[obj.id] = {
        ...this.addedAnimateObjects[obj.id],
        bottomLine: bottomLine,
      };
      this.addedAnimateObjects[obj.id] = {
        ...this.addedAnimateObjects[obj.id],
        topLine: topLine,
      };
      return {
        letters,
        lettersGroup,
        bottomLine,
        topLine,
      };
    }
    if (effect.type === HELLO_GOODBYE) {
      const leftLine = new fabric.Line(
        [
          obj.left -
            (obj.height * obj.scaleY * Math.sin((obj.angle * Math.PI) / 180)) /
              2,
          obj.top +
            (obj.height * obj.scaleY * Math.cos((obj.angle * Math.PI) / 180)) /
              2 -
            (obj.height * obj.scaleY) / 2,
          obj.left -
            (obj.height * obj.scaleY * Math.sin((obj.angle * Math.PI) / 180)) /
              2,
          obj.top +
            (obj.height * obj.scaleY * Math.cos((obj.angle * Math.PI) / 180)) /
              2 +
            (obj.height * obj.scaleY) / 2,
        ],
        {
          fill: obj.fill,
          stroke: obj.fill,
          strokeWidth: 4,
          originX: "left",
          originY: "center",
          selectable: false,
          angle: obj.angle,
          opacity: 0,
          visible: false,
        }
      );
      this.addedAnimateObjects[obj.id] = {
        ...this.addedAnimateObjects[obj.id],
        leftLine: leftLine,
      };
      return {
        letters,
        lettersGroup,
        leftLine,
      };
    }
    if (effect.type === GLITCH) {
      const { letters: letters1, lettersGroup: lettersGroup1 } =
        this.getCharactersAndGroup(obj, originalObject, effect);
      const { letters: letters2, lettersGroup: lettersGroup2 } =
        this.getCharactersAndGroup(obj, originalObject, effect);
      this.addedAnimateObjects[obj.id] = {
        ...this.addedAnimateObjects[obj.id],
        letters1: letters1,
      };
      this.addedAnimateObjects[obj.id] = {
        ...this.addedAnimateObjects[obj.id],
        letterGroup1: lettersGroup1,
      };
      this.addedAnimateObjects[obj.id] = {
        ...this.addedAnimateObjects[obj.id],
        letters2: letters2,
      };
      this.addedAnimateObjects[obj.id] = {
        ...this.addedAnimateObjects[obj.id],
        letterGroup2: lettersGroup2,
      };

      return {
        letters,
        lettersGroup,
        letters1,
        lettersGroup1,
        letters2,
        lettersGroup2,
      };
    }

    return {
      letters,
      lettersGroup,
    };
  };

  getGlitchAnimation = (obj, effect, addedObjects, loopEnabled, loopOption) => {
    const { letters, letters1, lettersGroup1, letters2, lettersGroup2 } =
      addedObjects;
    const canvas = this.handler.canvas;

    if (!letters1 || !letters1) {
      return [];
    }
    const radian = (-obj.angle * Math.PI) / 180;
    const cos = Math.cos(radian);
    const sin = Math.sin(radian);
    const run = (obj.width * obj.scaleX) / 2;
    const rise = (obj.height * obj.scaleY) / 2;
    lettersGroup1.set({
      originX: "center",
      originY: "center",
      left: cos * run + sin * rise + obj.left,
      top: cos * rise - sin * run + obj.top,
    });
    lettersGroup2.set({
      originX: "center",
      originY: "center",
      left: cos * run + sin * rise + obj.left,
      top: cos * rise - sin * run + obj.top,
    });
    // canvas.renderAll();

    const animation = {
      letterOpacity: 1,
      clipHeight: 0,
    };
    const animations = [];
    const loopCount = loopEnabled ? loopOption.trans_duration / 200 : 20;

    for (let i = 0; i < loopCount; i++) {
      let color = `rgb(${Math.random() * 255}, ${Math.random() * 255}, ${
        Math.random() * 255
      })`;
      let offsetX =
        Math.floor((Math.random() * 21 - 10) * obj.scaleX) +
        (obj.width * obj.scaleX) / 2;
      let offsetY =
        Math.floor((Math.random() * 21 - 10) * obj.scaleY) +
        (obj.height * obj.scaleY) / 2;
      let clipY = Math.floor((Math.random() * letters[1].height) / 2);
      let topY = Math.floor(Math.random() * letters[1].height);
      animations.push({
        option: {
          targets: animation,
          letterOpacity: 1,
          easing: effect.easing,
          duration: 40,
          update: () => {
            letters1.forEach((item, index) => {
              item.set({
                opacity: 0,
              });
            });
            // canvas.renderAll();
          },
        },
        offset: 200 * i,
      });
      animations.push({
        option: {
          targets: animation,
          letterOpacity: 1,
          clipHeight: [0, clipY],
          easing: effect.easing,
          duration: 60,
          update: () => {
            letters1.forEach((item, index) => {
              item.set({
                opacity: animation.opacity,
                fill: color,
              });
              item.clipPath = new fabric.Rect({
                width: item.width,
                height: animation.clipHeight,
                top: -topY / 2,
                left: -item.width / 2,
              });
            });
            lettersGroup1.set({
              left: obj.left + offsetX,
              top: obj.top + offsetY,
            });
            // canvas.renderAll();
          },
        },
        offset: 200 * i + 40,
      });
      animations.push({
        option: {
          targets: animation,
          letterOpacity: 1,
          easing: effect.easing,
          duration: 40,
          update: () => {
            letters1.forEach((item, index) => {
              item.set({
                opacity: 0,
              });
            });
            // canvas.renderAll();
          },
        },
        offset: 200 * i + 100,
      });
      color = `rgb(${Math.random() * 255}, ${Math.random() * 255}, ${
        Math.random() * 255
      })`;
      offsetX =
        Math.floor((Math.random() * 21 - 10) * obj.scaleX) +
        (obj.width * obj.scaleX) / 2;
      offsetY =
        Math.floor((Math.random() * 21 - 10) * obj.scaleY) +
        (obj.height * obj.scaleY) / 2;
      clipY = Math.floor((Math.random() * letters[1].height) / 2);
      topY = Math.floor(Math.random() * letters[1].height);
      animations.push({
        option: {
          targets: animation,
          letterOpacity: 1,
          clipHeight: [0, clipY],
          easing: effect.easing,
          duration: 40,
          update: () => {
            letters1.forEach((item, index) => {
              item.set({
                opacity: animation.opacity,
                fill: color,
              });
              item.clipPath = new fabric.Rect({
                width: item.width,
                height: animation.clipHeight,
                top: -topY / 2,
                left: -item.width / 2,
              });
            });
            lettersGroup1.set({
              left: obj.left + offsetX,
              top: obj.top + offsetY,
            });
            // canvas.renderAll();
          },
        },
        offset: 200 * i + 140,
      });
      animations.push({
        option: {
          targets: animation,
          letterOpacity: 1,
          easing: effect.easing,
          duration: 20,
          update: () => {
            letters1.forEach((item, index) => {
              item.set({
                opacity: 0,
              });
            });
            // canvas.renderAll();
          },
        },
        offset: 200 * i + 180,
      });
      animations.push({
        option: {
          targets: animation,
          letterOpacity: 1,
          easing: effect.easing,
          duration: 50,
          update: () => {
            letters.forEach((item, index) => {
              item.set({
                opacity: 1,
              });
            });
            // canvas.renderAll();
          },
        },
        offset: 0,
      });
    }

    for (let i = 0; i < loopCount; i++) {
      let color = `rgb(${Math.random() * 255}, ${Math.random() * 255}, ${
        Math.random() * 255
      })`;
      let offsetX =
        Math.floor((Math.random() * 21 - 10) * obj.scaleX) +
        (obj.width * obj.scaleX) / 2;
      let offsetY =
        Math.floor((Math.random() * 21 - 10) * obj.scaleY) +
        (obj.height * obj.scaleY) / 2;
      let clipY = Math.floor((Math.random() * letters[2].height) / 2);
      let topY = Math.floor(Math.random() * letters[2].height);
      animations.push({
        option: {
          targets: animation,
          letterOpacity: 1,
          easing: effect.easing,
          duration: 40,
          update: () => {
            letters2.forEach((item, index) => {
              item.set({
                opacity: 0,
              });
            });
            // canvas.renderAll();
          },
        },
        offset: 200 * i,
      });
      animations.push({
        option: {
          targets: animation,
          letterOpacity: 1,
          clipHeight: [0, clipY],
          easing: effect.easing,
          duration: 60,
          update: () => {
            letters2.forEach((item, index) => {
              item.set({
                opacity: animation.opacity,
                fill: color,
              });
              item.clipPath = new fabric.Rect({
                width: item.width,
                height: animation.clipHeight,
                top: -topY / 2,
                left: -item.width / 2,
              });
            });
            lettersGroup2.set({
              left: obj.left + offsetX,
              top: obj.top + offsetY,
            });
            // canvas.renderAll();
          },
        },
        offset: 200 * i + 40,
      });
      animations.push({
        option: {
          targets: animation,
          letterOpacity: 1,
          easing: effect.easing,
          duration: 40,
          update: () => {
            letters2.forEach((item, index) => {
              item.set({
                opacity: 0,
              });
            });
            // canvas.renderAll();
          },
        },
        offset: 200 * i + 100,
      });
      color = `rgb(${Math.random() * 255}, ${Math.random() * 255}, ${
        Math.random() * 255
      })`;
      offsetX =
        Math.floor((Math.random() * 21 - 10) * obj.scaleX) +
        (obj.width * obj.scaleX) / 2;
      offsetY =
        Math.floor((Math.random() * 21 - 10) * obj.scaleY) +
        (obj.height * obj.scaleY) / 2;
      clipY = Math.floor((Math.random() * letters[1].height) / 2);
      topY = Math.floor(Math.random() * letters[1].height);
      animations.push({
        option: {
          targets: animation,
          letterOpacity: 1,
          clipHeight: [0, clipY],
          easing: effect.easing,
          duration: 40,
          update: () => {
            letters2.forEach((item, index) => {
              item.set({
                opacity: animation.opacity,
                fill: color,
              });
              item.clipPath = new fabric.Rect({
                width: item.width,
                height: animation.clipHeight,
                top: -topY / 2,
                left: -item.width / 2,
              });
            });
            lettersGroup2.set({
              left: obj.left + offsetX,
              top: obj.top + offsetY,
            });
            // canvas.renderAll();
          },
        },
        offset: 200 * i + 140,
      });
      animations.push({
        option: {
          targets: animation,
          letterOpacity: 1,
          easing: effect.easing,
          duration: 20,
          update: () => {
            letters2.forEach((item, index) => {
              item.set({
                opacity: 0,
              });
            });
            // canvas.renderAll();
          },
        },
        offset: 200 * i + 180,
      });
    }

    return animations;
  };

  getDiscoAnimation = (
    obj,
    effect,
    addedObjects,
    animationTargets,
    loopEnabled,
    loopOption
  ) => {
    const animations = [];
    const { letters } = addedObjects;
    const canvas = this.handler.canvas;
    const loopCount = loopEnabled
      ? (loopOption.trans_duration - 1) / 200
      : (effect.trans_duration - 1) / 200;
    for (let i = 0; i < loopCount; i++) {
      let color = `rgb(${Math.floor(Math.random() * 255)}, ${Math.floor(
        Math.random() * 255
      )}, ${Math.floor(Math.random() * 255)})`;
      animations.push({
        option: {
          targets: animationTargets,
          [`${obj.id}_opacity`]: 1,
          easing: effect.easing,
          duration: 200,
          update: () => {
            letters.forEach((item, index) => {
              item.set({
                opacity: animationTargets[`${obj.id}_opacity`],
                fill: color,
              });
            });
            // canvas.renderAll();
          },
        },
        offset: 200 * i,
      });
    }
    return animations;
  };

  getHelloGoodByeAnimation = (
    obj,
    effect,
    addedObjects,
    animationTargets,
    animations
  ) => {
    const { letters, leftLine } = addedObjects;
    this.handler.canvas.add(leftLine);
    // this.handler.canvas.renderAll();

    this.animatedObjects[obj.id] = {
      ...this.animatedObjects[obj.id],
      opacity: obj.opacity,
    };
    const originX = leftLine.left;
    const originY = leftLine.top;
    const targets1 = {
      scaleY: 0,
      left: originX,
    };
    const targets2 = {
      translateX: 0,
    };

    const totalDuration = 2700;
    animationTargets[`${obj.id}_leftLine`] = 0.5;
    animations.push({
      option: {
        targets: animationTargets,
        [`${obj.id}_leftLine`]: [0, obj.opacity],
        easing: "easeOutExpo",
        duration: (1000 * effect.trans_duration) / totalDuration,
        update: () => {
          leftLine.set({
            opacity: animationTargets[`${obj.id}_leftLine`],
          });
          // this.handler.canvas.renderAll();
        },
      },
      offset: 0,
    });
    animations.push({
      option: {
        targets: targets1,
        scaleY: [0, 1],
        left: originX,
        easing: "easeOutExpo",
        duration: (1000 * effect.trans_duration) / totalDuration,
        update: () => {
          leftLine.set({
            left: targets1.left,
            scaleY: targets1.scaleY,
          });
          // this.handler.canvas.renderAll();
        },
      },
      offset: 0,
    });
    animations.push({
      option: {
        targets: targets2,
        translateX: [0, obj.width * obj.scaleX],
        easing: "easeOutExpo",
        duration: (700 * effect.trans_duration) / totalDuration,
        delay: (100 * effect.trans_duration) / totalDuration,
        update: () => {
          leftLine.set({
            left:
              originX +
              targets2.translateX * Math.cos((obj.angle * Math.PI) / 180),
            top:
              originY +
              targets2.translateX * Math.sin((obj.angle * Math.PI) / 180),
          });
          // this.handler.canvas.renderAll();
        },
      },
      offset: (1000 * effect.trans_duration) / totalDuration,
    });
    animations.push({
      option: {
        targets: animationTargets[`${obj.id}_lettersOpacity`],
        opacity: [0, obj.opacity],
        easing: "easeOutExpo",
        duration: (600 * effect.trans_duration) / totalDuration,
        delay: (el, i) =>
          (34 * (i + 1) * effect.trans_duration) / totalDuration,
        update: () => {
          letters.forEach((item, index) => {
            item.set({
              opacity:
                animationTargets[`${obj.id}_lettersOpacity`][index].opacity,
            });
          });
          // this.handler.canvas.renderAll();
        },
      },
      offset: (1000 * effect.trans_duration) / totalDuration,
    });
  };

  getCharacterLevelAnimations = (
    fabricObject,
    fabricObjectClone,
    effect,
    addedObjects,
    animationTargets,
    isSinglePreview,
    loopEnabled,
    loopOption
  ) => {
    const params = {
      fabricObject: addedObjects.lettersGroup,
      fabricObjectClone,
      effect,
      animatedObjects: this.animatedObjects,
      loopEnabled,
      loopOption,
      workarea: this.handler.workarea,
      isSinglePreview,
      storeOriginalProperties: false,
      extras: this.getExtraAnimationParams(
        fabricObject,
        effect.type,
        isSinglePreview,
        addedObjects
      ),
    };

    /**
     * Post animation tasks for character based animations
     */
    if (!isSinglePreview) {
      this.tasks.push({
        task: this.revealOriginalText,
        args: [fabricObject, fabricObjectClone.opacity],
      });
    }

    switch (effect.type) {
      case GLITCH:
        return this.getGlitchAnimation(
          fabricObject,
          effect,
          addedObjects,
          loopEnabled,
          loopOption
        );
      case DISCO:
        return this.getDiscoAnimation(
          fabricObject,
          effect,
          addedObjects,
          animationTargets,
          loopEnabled,
          loopOption
        );
      case GREAT_THINKER:
      case SUNNY_MORNINGS:
      case RISING_STRONG_IN:
      case RISING_STRONG_OUT:
      case MADE_WITH_LOVE:
      case BEAUTIFUL_QUESTIONS:
      case DOMINO_DREAMS:
      case COFFEE_MORNINGS:
      case A_NEW_PRODUCTION_IN:
      case A_NEW_PRODUCTION_OUT:
      case FIND_YOUR_ELEMENT:
      case THURSDAY:
      case HELLO_GOODBYE:
        return getAnimationConfig(params);
      default:
        return [];
    }
  };

  getExtraAnimationParams = (
    fabricObject,
    type,
    isSinglePreview,
    extraObjects
  ) => {
    switch (type) {
      case BLOCK_REVEAL_IN_TOP:
      case BLOCK_REVEAL_IN_BOTTOM:
      case BLOCK_REVEAL_IN_LEFT:
      case BLOCK_REVEAL_IN_RIGHT:
      case BLOCK_REVEAL_OUT_TOP:
      case BLOCK_REVEAL_OUT_BOTTOM:
      case BLOCK_REVEAL_OUT_LEFT:
      case BLOCK_REVEAL_OUT_RIGHT:
        const extraAnimationParams =
          this.getExtraAnimationParamsForBlockReveal(fabricObject);

        if (!isSinglePreview) {
          this.extraAnimationObjects.push(extraAnimationParams.objects[0]);
          this.tasks.push({ task: this.removeExtraAnimationObjects, args: [] });
        }

        return extraAnimationParams;
      case GREAT_THINKER:
      case SUNNY_MORNINGS:
      case RISING_STRONG_IN:
      case RISING_STRONG_OUT:
      case MADE_WITH_LOVE:
      case BEAUTIFUL_QUESTIONS:
      case DOMINO_DREAMS:
      case COFFEE_MORNINGS:
      case A_NEW_PRODUCTION_IN:
      case A_NEW_PRODUCTION_OUT:
        return { canvas: this.handler.canvas, objects: [fabricObject] };
      case FIND_YOUR_ELEMENT:
        return {
          canvas: this.handler.canvas,
          objects: [fabricObject, extraObjects.bottomLine],
        };
      case THURSDAY:
        return {
          canvas: this.handler.canvas,
          objects: [
            fabricObject,
            extraObjects.bottomLine,
            extraObjects.topLine,
          ],
        };
      case HELLO_GOODBYE:
        return {
          canvas: this.handler.canvas,
          objects: [fabricObject, extraObjects.leftLine],
        };
      default:
        return {};
    }
  };

  getExtraAnimationParamsForBlockReveal = (fabricObject) => {
    const block = new fabric.Rect({
      height: fabricObject.height * fabricObject.scaleY,
      width: fabricObject.width * fabricObject.scaleX,
      top: fabricObject.top,
      left: fabricObject.left,
      skewX: fabricObject.skewX,
      skewY: fabricObject.skewY,
      fill:
        fabricObject.fill !== "rgba(0,0,0,0)"
          ? fabricObject.fill
          : fabricObject.textBackgroundColor !== "rgba(0,0,0,0)"
          ? fabricObject.textBackgroundColor
          : fabricObject.stroke,
      selectable: false,
      opacity: 0,
    });

    setDefaultClipPath(block);
    return {
      objects: [block],
      canvas: this.handler.canvas,
    };
  };

  doPostAnimationTasks = () => {
    for (const task of this.tasks) {
      task.task(...task.args);
    }

    this.tasks = [];
  };

  removeExtraAnimationObjects = () => {
    for (const object of this.extraAnimationObjects) {
      this.handler.canvas.remove(object);
    }

    this.extraAnimationObjects = [];
  };

  revealOriginalText = (originalTextObject, opacity) => {
    originalTextObject.set("opacity", opacity);
  };
}

export default AnimationHandler;
